// Read-only signal chips and empty-section evidence for the Updates reader.
// Derived from the same selected-week project payload the sections render;
// unknown evidence stays "unknown" (never zero) and no money is exposed.
import { assignedTrackCompleteness } from "../../../lib/weeklyTrackCompleteness.js";
import { weeklyText } from "../../../lib/weeklyRichText.js";
import { milestoneText } from "./weeklyModel.js";

const isOpen = (row) => !row?.status || row.status === "open";
export const FINANCIAL_MEASURES = ["budget", "spendToDate", "forecast", "variance"];
export const hasFinancialValue = (value) => value != null && String(value).trim() !== "" && Number.isFinite(Number(value));

export function openDecisions(project) {
  return Array.isArray(project?.decisions) ? project.decisions.filter(isOpen) : null;
}
export function openRisks(project) {
  return Array.isArray(project?.risks) ? project.risks.filter(isOpen) : null;
}

// Qualitative only: sign of the recorded variance, or why it is unknown.
export function varianceSignal(financials) {
  if (!financials) return { value: "Unavailable", tone: "unknown" };
  const raw = financials.variance;
  if (raw == null || raw === "" || !Number.isFinite(Number(raw))) return { value: "Not recorded", tone: "unknown" };
  const n = Number(raw);
  if (n < 0) return { value: "Over budget", tone: "warn" };
  if (n > 0) return { value: "Under budget", tone: "ok" };
  return { value: "On budget", tone: "ok" };
}

export function readerSignals(project) {
  const tracks = assignedTrackCompleteness(project);
  const decisions = openDecisions(project);
  const risks = openRisks(project);
  const milestone = project?.nextMilestone ? milestoneText(project.nextMilestone) : null;
  return [
    { key: "tracks", label: "Posted tracks", value: tracks.available ? `${tracks.inCount} of ${tracks.total}` : "Unknown", tone: tracks.available ? (tracks.total > 0 && tracks.inCount === tracks.total ? "ok" : "neutral") : "unknown",
      detail: tracks.available ? (tracks.quietCount ? `${tracks.quietCount} quiet` : null) : "assignments not captured" },
    { key: "decisions", label: "Decisions", value: decisions ? String(decisions.length) : "Unknown", tone: decisions ? (decisions.length ? "neutral" : "ok") : "unknown", detail: decisions ? "open" : null },
    { key: "risks", label: "Risks", value: risks ? String(risks.length) : "Unknown", tone: risks ? (risks.length ? "warn" : "ok") : "unknown", detail: risks ? "open" : null },
    { key: "variance", label: "Variance", ...varianceSignal(project?.financials) },
    { key: "milestone", label: "Next milestone", value: milestone || (project?.nextMilestone === null ? "None scheduled" : "Unavailable"), tone: milestone ? "neutral" : "unknown" },
  ];
}

// Genuinely empty = evidence is present and contains nothing. Missing archive
// evidence, restricted or failed data are NOT empty and never collapse.
export function sectionEmptiness(project) {
  const s = project?.summary || {};
  const captured = Object.hasOwn(project || {}, "summary");
  const decisions = openDecisions(project);
  const risks = openRisks(project);
  return {
    thisWeek: captured && !weeklyText(s.accomplishments).trim() && !weeklyText(s.upcoming).trim() && !weeklyText(s.blockers).trim(),
    decisions: Array.isArray(decisions) && decisions.length === 0,
    risks: Array.isArray(risks) && risks.length === 0,
    financials: Boolean(project?.financials) && !FINANCIAL_MEASURES.some(key => hasFinancialValue(project.financials[key])),
    pmo: captured && !weeklyText(s.pmoNote).trim(),
  };
}

export const EMPTY_BAR_TEXT = {
  thisWeek: "This week · not posted",
  decisions: "Decisions · 0 — none open",
  risks: "Risks · 0 — none this week",
  pmo: "PMO note · not posted",
};

export const EMPTY_SECTION_MESSAGE = {
  thisWeek: "not posted",
  financials: "not recorded",
  decisions: "none",
  risks: "none",
};
